import { Fragment } from 'react'
import styled from 'styled-components'
import {
    useTranslate,
    useNavigate,
    usePages,
    useRefundMethods,
    useStoreProducts
} from '@/hooks'
import { useTheme } from '@itsrever/design-system'
import { useAppSelector } from '@/redux/hooks'
import { Button } from '@itsrever/design-system'
import {
    NonReturnableProductCard,
    OrderInformation,
    OrderItem
} from '@/components/ItemSelectionComponents'
import { Subproduct } from '@/redux/order/orderSlice'
import { ItemActions, RefundActions } from '@/redux/return/returnSlice'
import { LoadingModal } from '../Loading'
import { usePostHog } from '@/application/use-posthog'
import TopBar from '@/components/TopBarComponent/TopBar'
import HoffTopBar from '../HoffTopBar/hoff-top-bar'
import { useFeatureFlag } from '@/application/use-feature-flag'
import { BlockedFromReturnProductCard } from './BlockedFromReturnProductCard'
import { useProductsBrands } from '@/hooks/use-products-brands'
import { storeMenuQuery } from '@/hooks/use-store-menu'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { DEFAULT_COUNTRY_CODE } from '@/app/full-catalog/helpers/constants'

const USE_MULTILABEL_RETURN_ID = 1915

export const Order: React.FC = () => {
    const theme = useTheme()

    const { i18n } = useTranslation()
    const { translate } = useTranslate()
    const { navigateRelative } = useNavigate()
    const { pages } = usePages()

    const { captureEventPosthog } = usePostHog()

    const { isFlagEnabled: isMultilabelReturnEnabled } = useFeatureFlag(
        USE_MULTILABEL_RETURN_ID
    )

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const isHoff = settings.ecommerce_group === 'hoff'
    const isMisako = settings.ecommerce_group == 'misako'
    const isTheBradery = settings.ecommerce_group === 'thebradery'

    const order = useAppSelector((store) => store.order)

    const countryCode =
        useAppSelector((store) => store.return.returnAddress.country_code) ??
        DEFAULT_COUNTRY_CODE

    const { data } = useProductsBrands({
        orderNumber: order.orderNumber,
        email: order.email,
        ecommerceId:
            isMultilabelReturnEnabled && isTheBradery
                ? settings.ecommerce_id
                : undefined
    })

    const exchangeFlow = useAppSelector(
        (store) => store.settingsApi.settings.response.exchange_flow
    )

    captureEventPosthog('Initial', {
        isFullCatalogEnabled: exchangeFlow === 'FULL',
        ecommerceId: settings.ecommerce_id
    })

    const products = order.orderProducts

    const { callRefundMethods, loadingRefundMethods } = useRefundMethods(
        handleNavigateNextPage
    )
    const returnAddress = useAppSelector((store) => store.return.returnAddress)

    const { data: menu, status } = useQuery({
        ...storeMenuQuery({
            ecommerceId: settings.ecommerce_id,
            language: i18n.language,
            countryCode
        }),
        enabled: exchangeFlow === 'FULL'
    })

    useStoreProducts(menu?.items ?? [], 0, '', status === 'success')

    const returnedItems = useAppSelector((store) => store.return.returnedItems)
    const returnedItemsCount = returnedItems.reduce((acc, item) => {
        if (item.item.action === RefundActions.ToReturn)
            return acc + item.item.quantity
        return acc
    }, 0)

    const exchangedOneOnOneItemsCount = returnedItems.reduce((acc, item) => {
        if (item.action === ItemActions.ToExchangeOneOnOne)
            return acc + item.item.quantity
        return acc
    }, 0)

    const exchangedFullCatalogItemsCount = returnedItems.reduce((acc, item) => {
        if (item.action === ItemActions.ToExchangeFullCatalog)
            return acc + item.item.quantity
        return acc
    }, 0)

    const onlyNotReceived = returnedItems.every(
        (retItem) => retItem.item.product_return_reason == 'NOT_RECEIVED'
    )

    const onlyExchanges = returnedItems.every(
        (item) =>
            item.action === ItemActions.ToExchangeOneOnOne ||
            item.action === ItemActions.ToExchangeFullCatalog
    )

    function handleNavigateNextPage() {
        if (settings.ecommerce_group === 'twojeys') {
            const vetoReasons = ['DEFECTIVE', 'WRONG_ITEM']
            const isVeto = returnedItems.some((item) =>
                vetoReasons.includes(item.item.product_return_reason)
            )
            if (isVeto) {
                navigateRelative(pages.RefundMethod)
                return
            }
        }

        if (onlyNotReceived) {
            if (onlyExchanges) {
                navigateRelative(pages.ExchangeAddress)
            } else {
                navigateRelative(pages.RefundMethod)
            }
        } else {
            navigateRelative(pages.ReturnAddress)
        }
    }

    const handleClickContinue = () => {
        captureEventPosthog('Returned Items', {
            returnedItemsCount,
            exchangedOneOnOneItemsCount,
            exchangedFullCatalogItemsCount,
            ecommerceId: settings.ecommerce_id
        })
        callRefundMethods(returnAddress)
    }

    function isNonReturnableProduct(prod: Subproduct): boolean {
        const product = prod.product

        return (
            (product.non_returnable_items &&
                product.non_returnable_items.length > 0) ??
            false
        )
    }

    const shouldBlockProductReturnByBrand = ({ product }: Subproduct) => {
        const firstSelectedProduct = returnedItems[0]

        const productsBrands = data?.products_and_brands ?? []

        const firstSelectedItemBrand = firstSelectedProduct
            ? productsBrands.find(
                  (product) =>
                      product.product_id === firstSelectedProduct.productId
              )?.brand_name
            : undefined

        const productsIdsNotAllowedToReturn = productsBrands
            .filter((product) => product.brand_name !== firstSelectedItemBrand)
            .map((product) => product.product_id)

        return (
            firstSelectedProduct &&
            product.product?.id &&
            productsIdsNotAllowedToReturn.includes(product.product.id)
        )
    }

    if (isHoff) {
        return (
            <>
                {loadingRefundMethods ? <LoadingModal loading /> : null}
                <TopBar page="order" />
                <div className="relative mx-auto flex size-full h-full max-w-screen-xl grow flex-col items-center bg-cover bg-center bg-no-repeat px-2 md:px-10">
                    <HoffTopBar />
                    <h1
                        className=" w-full pb-1 pt-6 font-haas text-lg font-normal text-neutral-800 lg:text-lg"
                        dangerouslySetInnerHTML={{
                            __html: translate('item_selection_title')
                        }}
                    />
                    <div className="size-full rounded-xl bg-white/80 backdrop-blur-sm">
                        <div className="mt-4 flex flex-col border-b-1 border-solid border-black ">
                            {products.map((prod, i) => (
                                <Fragment key={i}>
                                    {isNonReturnableProduct(prod) ? (
                                        <NonReturnableProductCard
                                            product={prod.product}
                                        />
                                    ) : (
                                        <OrderItem
                                            orderItem={prod.product}
                                            subProductId={prod.subProductId}
                                        />
                                    )}
                                </Fragment>
                            ))}
                        </div>
                        <div className="mb-10 mt-6 flex w-full">
                            <button
                                id="item-selection-continue-button"
                                onClick={handleClickContinue}
                                disabled={!returnedItems.length}
                                className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 font-haas text-sm text-white disabled:bg-neutral-500"
                            >
                                {translate('item_selection_button_continue')}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title

    if (isMisako) {
        return (
            <>
                {loadingRefundMethods ? <LoadingModal loading /> : null}
                <div className="flex h-full min-h-full flex-1 flex-col gap-4 bg-white p-4 md:px-10 md:py-6">
                    <div className="mb-auto flex w-full items-end">
                        {logoUrl ? (
                            <div className="mr-auto">
                                <img
                                    style={
                                        {
                                            '--logo-desktop-height': '1.5rem',
                                            '--logo-mobile-height': '1.2rem'
                                        } as React.CSSProperties
                                    }
                                    width={285}
                                    height={70}
                                    src={logoUrl}
                                    alt={title}
                                    className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                                />
                            </div>
                        ) : null}
                        <div className="flex items-center gap-5">
                            <a
                                href="https://www.misako.com/pages/wishlist"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <img
                                    src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                                    width={20}
                                    height={20}
                                />
                            </a>
                            <a
                                href="https://www.misako.com/account"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="20"
                                    height="21"
                                    viewBox="0 0 20 21"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                                        stroke="rgb(21, 21, 21)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                    <path
                                        d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                                        stroke="rgb(21, 21, 21)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                            </a>
                            <a
                                href="https://www.misako.com/collections/top-ventas"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 19 19"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                                        stroke="rgb(21, 21, 21)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                            </a>
                        </div>
                    </div>
                    <div className="flex flex-1 flex-col">
                        <h1
                            className="mt-5 text-lg font-[300] tracking-wider text-neutral-600 [font-family:Inter] lg:text-xl"
                            dangerouslySetInnerHTML={{
                                __html: translate('item_selection_title')
                            }}
                        />
                        <OrderInformation />
                        <div className="mt-4 flex flex-wrap gap-4 border-solid border-grey-3 md:gap-8">
                            {products.map((prod, i) => (
                                <Fragment key={i}>
                                    {isNonReturnableProduct(prod) ? (
                                        <NonReturnableProductCard
                                            product={prod.product}
                                        />
                                    ) : (
                                        <OrderItem
                                            orderItem={prod.product}
                                            subProductId={prod.subProductId}
                                        />
                                    )}
                                </Fragment>
                            ))}
                        </div>
                        <div className="mt-auto flex w-full justify-center pt-8">
                            <div className="w-full md:max-w-[180px]">
                                <Button
                                    id="item-selection-continue-button"
                                    onClick={handleClickContinue}
                                    disabled={!returnedItems.length}
                                    fullWidth
                                >
                                    <span className="text-[13px] font-[300] uppercase tracking-wide [font-family:Inter]">
                                        {translate(
                                            'item_selection_button_continue'
                                        )}
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            {loadingRefundMethods ? <LoadingModal loading /> : null}
            <TopBar page="order" />
            <MainDiv color={theme.colors.grey[5]}>
                <div className="w-10/12 max-w-[800px] md:w-8/12">
                    <p
                        className="mt-5 text-center text-lg font-normal text-neutral-600 lg:text-xl"
                        dangerouslySetInnerHTML={{
                            __html: translate('item_selection_title')
                        }}
                    />
                    <div className="mt-6">
                        <OrderInformation />
                    </div>
                    <div className="mt-4 flex flex-col gap-4 border-solid border-grey-3">
                        {products.map((prod, i) => {
                            const firstSelectedProduct = returnedItems[0]

                            return (
                                <Fragment key={i}>
                                    {isNonReturnableProduct(prod) ? (
                                        <NonReturnableProductCard
                                            product={prod.product}
                                        />
                                    ) : isMultilabelReturnEnabled &&
                                      firstSelectedProduct &&
                                      shouldBlockProductReturnByBrand(prod) ? (
                                        <BlockedFromReturnProductCard
                                            product={prod.product}
                                        />
                                    ) : (
                                        <OrderItem
                                            orderItem={prod.product}
                                            subProductId={prod.subProductId}
                                        />
                                    )}
                                </Fragment>
                            )
                        })}
                    </div>
                    <div className="mb-10 mt-6 flex w-full justify-center">
                        <div className="w-full md:max-w-[280px]">
                            <Button
                                id="item-selection-continue-button"
                                onClick={handleClickContinue}
                                disabled={!returnedItems.length}
                                fullWidth
                            >
                                {translate('item_selection_button_continue')}
                                {returnedItemsCount > 0 &&
                                    ' (' + returnedItemsCount + ')'}
                            </Button>
                        </div>
                    </div>
                </div>
            </MainDiv>
        </>
    )
}

const MainDiv = styled.div<{ color: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-grow: 1;
    background-color: ${(p) => p.color};
`
